import { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { BookingResponse } from '../../types';
import { getTimezoneAbbreviation } from '../../utils/timezone';
import { downloadICS, getGoogleCalendarUrl, getOutlookCalendarUrl } from '../../utils/calendar';
import Button from '../Button';
import Card from '../Card';

interface Step5Props {
  booking: BookingResponse;
}

export default function Step5Confirmation({ booking }: Step5Props) {
  const startDate = parseISO(booking.slot.start);
  const endDate = parseISO(booking.slot.end);

  const dateStr = formatInTimeZone(startDate, booking.tz, 'EEEE, MMMM d, yyyy');
  const timeStr = formatInTimeZone(startDate, booking.tz, 'h:mm a');
  const endTimeStr = formatInTimeZone(endDate, booking.tz, 'h:mm a');
  const tzAbbr = getTimezoneAbbreviation(booking.tz, startDate);

  const calendarEvent = {
    title: 'NextAgency Meeting',
    description: `Meeting with ${booking.providerName} from NextAgency`,
    location: 'Virtual Meeting',
    start: startDate,
    end: endDate,
    timezone: booking.tz
  };

  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2xl)',
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto',
    textAlign: 'center'
  };

  const successIconStyles: CSSProperties = {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--color-success) 0%, #34d399 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'var(--spacing-lg)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const subtitleStyles: CSSProperties = {
    fontSize: '1.125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6
  };

  const detailsCardStyles: CSSProperties = {
    textAlign: 'left'
  };

  const detailRowStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 'var(--spacing-md) 0',
    borderBottom: '1px solid var(--color-border)'
  };

  const labelStyles: CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const valueStyles: CSSProperties = {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    textAlign: 'right'
  };

  const buttonsContainerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)'
  };

  const calendarButtonsStyles: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 'var(--spacing-md)'
  };

  const noteStyles: CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--color-text-secondary)',
    padding: 'var(--spacing-lg)',
    background: 'var(--na-gray-50)',
    borderRadius: 'var(--radius-md)',
    lineHeight: 1.6
  };

  return (
    <motion.div
      style={containerStyles}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Success Icon with Checkmark */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
      >
        <div style={successIconStyles}>
          <motion.svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <motion.path d="M20 6L9 17l-5-5" />
          </motion.svg>
        </div>
      </motion.div>

      {/* Title and Subtitle */}
      <div>
        <motion.h1
          style={titleStyles}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          You're All Set!
        </motion.h1>
        <motion.p
          style={subtitleStyles}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Your meeting has been confirmed. We've sent a confirmation email with all the details.
        </motion.p>
      </div>

      {/* Booking Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card hoverable={false} style={detailsCardStyles}>
          <div style={detailRowStyles}>
            <span style={labelStyles}>Date</span>
            <span style={valueStyles}>{dateStr}</span>
          </div>
          <div style={detailRowStyles}>
            <span style={labelStyles}>Time</span>
            <span style={valueStyles}>
              {timeStr} - {endTimeStr} {tzAbbr}
            </span>
          </div>
          <div style={detailRowStyles}>
            <span style={labelStyles}>With</span>
            <span style={valueStyles}>{booking.providerName}</span>
          </div>
          {booking.confirmationNumber && (
            <div style={{ ...detailRowStyles, borderBottom: 'none' }}>
              <span style={labelStyles}>Confirmation</span>
              <span style={valueStyles}>{booking.confirmationNumber}</span>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Calendar Buttons */}
      <motion.div
        style={buttonsContainerStyles}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)' }}>
          Add to Your Calendar
        </h3>
        <div style={calendarButtonsStyles}>
          <Button
            onClick={() => window.open(getGoogleCalendarUrl(calendarEvent), '_blank')}
            variant="outline"
          >
            Google Calendar
          </Button>
          <Button
            onClick={() => window.open(getOutlookCalendarUrl(calendarEvent), '_blank')}
            variant="outline"
          >
            Outlook
          </Button>
          <Button onClick={() => downloadICS(calendarEvent)} variant="outline">
            Download .ics
          </Button>
        </div>
      </motion.div>

      {/* Additional Actions */}
      {(booking.rescheduleUrl || booking.cancelUrl) && (
        <motion.div
          style={calendarButtonsStyles}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          {booking.rescheduleUrl && (
            <Button
              onClick={() => (window.location.href = booking.rescheduleUrl!)}
              variant="outline"
            >
              Reschedule
            </Button>
          )}
          {booking.cancelUrl && (
            <Button
              onClick={() => (window.location.href = booking.cancelUrl!)}
              variant="outline"
            >
              Cancel Booking
            </Button>
          )}
        </motion.div>
      )}

      {/* Note */}
      <motion.div
        style={noteStyles}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <strong>What's Next?</strong>
        <br />
        You'll receive a meeting invitation via email shortly with a video conference link. If you
        have any questions before the meeting, feel free to contact us.
      </motion.div>
    </motion.div>
  );
}
